import type { Session, User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { isSupabaseConfigured, supabase } from '../../shared/api/supabase';
import type { Database } from '../../shared/types/database.types';

type Profile = Database['public']['Tables']['profiles']['Row'];

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  schemaReady: boolean;
  isManager: boolean;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [schemaReady, setSchemaReady] = useState(true);
  const currentUserId = useRef<string | null>(null);

  const loadProfile = useCallback(async (userId: string | null) => {
    if (!supabase || !userId) {
      setProfile(null);
      return;
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('id, role, display_name, email, student_id, created_at, updated_at')
      .eq('id', userId)
      .maybeSingle();

    if (currentUserId.current !== userId) return;

    if (error) {
      setSchemaReady(false);
      setProfile(null);
      return;
    }

    setSchemaReady(true);
    setProfile(data);
  }, []);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    let active = true;
    const { data } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!active) return;

      const nextUserId = nextSession?.user.id ?? null;
      const identityChanged = currentUserId.current !== nextUserId;
      const isInitialSession = event === 'INITIAL_SESSION';

      if (identityChanged) {
        setProfile(null);
        queryClient.clear();
      }
      currentUserId.current = nextUserId;
      setSession(nextSession);

      // SIGNED_IN and TOKEN_REFRESHED may fire again when a background tab becomes
      // active. Keep the current page mounted when the user identity did not change.
      if (!isInitialSession && !identityChanged && event !== 'USER_UPDATED') return;

      const shouldBlockUi = isInitialSession || identityChanged;
      if (shouldBlockUi) setLoading(true);

      window.setTimeout(() => {
        void loadProfile(nextUserId).finally(() => {
          if (active && shouldBlockUi && currentUserId.current === nextUserId) setLoading(false);
        });
      }, 0);
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [loadProfile, queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      loading,
      schemaReady,
      isManager: profile?.role === 'admin' || profile?.role === 'editor',
      refreshProfile: () => loadProfile(session?.user.id ?? null),
      signOut: async () => {
        if (!supabase) return;
        currentUserId.current = null;
        setProfile(null);
        setSession(null);
        setLoading(false);
        queryClient.clear();
        await supabase.auth.signOut({ scope: 'local' });
      },
    }),
    [loadProfile, loading, profile, queryClient, schemaReady, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
